import { CirclePlusIcon } from 'lucide-react';
import * as React from 'react';

import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { Separator } from 'src/components/ui/separator';
import { cn } from 'src/lib/utils';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface FilterButtonProps {
  title: string;
  selectedLabels?: string[];
  className?: string;
  icon?: React.ReactNode;
}

export const FilterButton = React.forwardRef<HTMLButtonElement, FilterButtonProps>(
  ({ title, selectedLabels = [], className, icon, ...props }, ref) => {
    return (
      <Tooltip>
        <TooltipTrigger
          asChild
          /**
           * 获得焦点时 不必要让tooltip显示
           * 这么处理是因为 当sheet关闭时 sheet会将焦点给tooltip
           * 这时tooltip就在页面显示且不会消失 除非再次hover
           */
          onFocus={(e) => {
            e.preventDefault();
          }}
        >
          <Button
            ref={ref}
            variant="outline"
            size="sm"
            className={cn('border-dashed gap-2', className)}
            {...props}
          >
            {icon || <CirclePlusIcon className="h-4 w-4" />}
            <div className="lg:flex items-center h-full hidden">
              {title}
              {selectedLabels?.length > 0 && (
                <>
                  <Separator orientation="vertical" className="mx-2 h-4 shrink-0" />
                  <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden">
                    {selectedLabels.length}
                  </Badge>
                  <div className="flex items-center gap-1">
                    {selectedLabels.length > 2 ? (
                      <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                        {selectedLabels.length} 项过滤
                      </Badge>
                    ) : (
                      selectedLabels.map((label, index) => {
                        // 使用正则表达式精确匹配日期范围格式：YYYY-MM-DD~YYYY-MM-DD
                        const dateRangeRegex = /^\d{4}-\d{2}-\d{2}~\d{4}-\d{2}-\d{2}$/;
                        const isDateRange = dateRangeRegex.test(label);
                        return (
                          <Badge
                            variant="secondary"
                            key={index}
                            className="rounded-sm px-1 font-normal"
                          >
                            <span
                              className={
                                isDateRange
                                  ? undefined
                                  : 'max-w-18 xl:max-w-24 overflow-hidden text-ellipsis whitespace-nowrap text-nowrap'
                              }
                            >
                              {label}
                            </span>
                          </Badge>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="flex lg:hidden items-center gap-1">
          {title}
          {selectedLabels.length > 0 && (
            <>
              <Separator
                orientation="vertical"
                className="mx-1 data-[orientation=vertical]:h-2 shrink-0"
              />
              {selectedLabels.length > 2 ? (
                <span>{selectedLabels.length} 项过滤</span>
              ) : (
                selectedLabels.map((label, index) => <span key={index}>{label}</span>)
              )}
            </>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }
);

FilterButton.displayName = 'FilterButton';
